import { BillingDataPlatform } from 'reactApp/api/billing/data/BillingDataAPICall.types';
import { billingDataEndpointEnabled } from 'reactApp/appHelpers/featuresHelpers/features/billingDataEndpoint';
import { type BillingDataSubscriptionInfo, BillingDataSubscriptionInfoStatus } from 'reactApp/modules/billingData/billingData.types';
import { isVkMusicReward } from 'reactApp/modules/promoTariffs/promoTariffs.types';
import type { Product } from 'reactApp/types/Billing';
import { formatDefaultDate, formatFileHistoryDate } from 'reactApp/utils/formatDate';
import {
    getPeriodNameAsWord,
    getTrialOrDiscountDateEnd,
    getTypeOfPeriodName,
    isMonth3Period,
    isMonth6Period,
    isYear2Period,
    nowIsTrialOrDiscountPeriod,
} from 'reactApp/utils/Period';
import { formatPrice } from 'reactApp/utils/priceHelpers';

export const getExpiresTitle = (expires: number, status?: BillingDataSubscriptionInfoStatus) =>
    `${status === BillingDataSubscriptionInfoStatus.SUSPENDED ? '' : 'до '}${formatDefaultDate(expires)}`;

export const getTariffConditionsText = (subscription: BillingDataSubscriptionInfo & { purchaseCost?: number }, product: Product) => {
    const isTrialPeriod = product?.trialPeriod && nowIsTrialOrDiscountPeriod(product.trialPeriod, subscription.start);
    const renewalCost = subscription.renewalCost;

    if (isVkMusicReward(subscription)) {
        return subscription?.space?.value || '';
    }

    let productPeriodSuffix =
        isYear2Period(product?.period) || isMonth6Period(product?.period)
            ? '— разовый платёж'
            : `в ${getTypeOfPeriodName(product?.period)}`;

    if (isMonth3Period(product?.period)) {
        productPeriodSuffix = 'на 3 месяца';
    }

    if ((subscription?.isTrial ?? isTrialPeriod) && product?.trialPeriod) {
        const trialDateEnd = getTrialOrDiscountDateEnd(product.trialPeriod, subscription.start);

        const discountCost = billingDataEndpointEnabled ? renewalCost : product?.discountPrice;
        const freeCondition = `${getPeriodNameAsWord(product?.trialPeriod, false, true)} бесплатно`;
        const discountCondition =
            product?.discountPeriod && `затем ${formatPrice(discountCost, 'ru')} ₽ в ${getTypeOfPeriodName(product.discountPeriod)}`;
        const defaultCondition = `${formatPrice(renewalCost, 'ru')} ₽ в ${getTypeOfPeriodName(product?.period)}`;
        // subscriptions/list и billing/data не возвращают эту информацию
        const upsaleTrialAferDateCondition = `${formatPrice(product.price, 'ru')} ₽ в ${getTypeOfPeriodName(product.period)}`;

        if (discountCondition) {
            const discountDateEnd = getTrialOrDiscountDateEnd(product?.discountPeriod, trialDateEnd);
            return `${freeCondition}, ${discountCondition},\nс ${formatFileHistoryDate(
                discountDateEnd.valueOf()
            )} — ${upsaleTrialAferDateCondition}`;
        }

        return `${freeCondition}, затем ${defaultCondition}`;
    } else if (subscription.id !== BillingDataPlatform.base) {
        const periodLetter = product?.period?.slice(-1);
        let period = product?.discountPeriodCount ? `${product.discountPeriodCount}${periodLetter}` : product?.discountPeriod;
        const isDiscountPeriod = product?.discountPeriod && nowIsTrialOrDiscountPeriod(period, subscription.start);
        const discountCost = billingDataEndpointEnabled ? subscription.purchaseCost : product?.discountPrice;

        /**
         * tempexp_17360-next-line
         * @TODO: CLOUDWEB-17360: Нестандартный скидочный период - 13 месяцев.
         * По дизайну надо показыва инфо про 1 год + лейбл про подарок в 1 месяц.
         * Сделать нормальное решение после АБ.
         */
        if (period === '13m') {
            period = '12m';
        }

        return product?.discountPeriod && discountCost && isDiscountPeriod
            ? `${getPeriodNameAsWord(period || '', false, true)} за ${formatPrice(discountCost, 'ru')} ₽, затем ${formatPrice(
                  /** для составных периодов скидки, мы в renewalCost получаем цену следующего СКИДОЧНОГО продления,
                   *  а не ЦЕНОЙ после скидок*/
                  product?.discountPeriodCount ? product?.price : renewalCost,
                  'ru'
              )} ₽ в ${getTypeOfPeriodName(product?.period)}`
            : `${formatPrice(renewalCost, 'ru')} ₽ ${productPeriodSuffix}`;
    }

    return `${formatPrice(renewalCost, 'ru')}₽ ${productPeriodSuffix}`;
};

export const getCardNumber = <T extends { number: string }>(card: T | null) => card && `Карта ∙∙${card.number.slice(-4)}`;

export const getNextPayment = (renewAt?: number, expires?: number, paused?: boolean) => {
    if (paused && renewAt) {
        return `${formatFileHistoryDate(renewAt)}, возобновится после паузы`;
    }

    if (renewAt) {
        return formatFileHistoryDate(renewAt);
    }

    if (expires) {
        return formatFileHistoryDate(expires);
    }

    return '';
};
